import { HeidiLogoMark } from '@heidi/design-system/heidi-logo-mark';
import { focusRing } from '@heidi/design-system/styles';
import { TypographyCaption, TypographyH4 } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import type { MessageDescriptor } from 'react-intl';
import { useIntl } from 'react-intl';

import { voiceWorkspaceMessages as messages } from './workspace.messages';

/**
 * Idle Voice quick-starts — Figma “before anything has been said” chips.
 * @see https://www.figma.com/design/5y5VGXMkw2812jPfNuNBS9/Alastair-s-Playground?node-id=305-31441
 */
export type WorkspaceQuickStartKind =
  | 'start-session'
  | 'brainstorm'
  | 'dictate-notes'
  | 'capture-work';

interface QuickStart {
  kind: WorkspaceQuickStartKind;
  label: MessageDescriptor;
}

const QUICK_STARTS: QuickStart[] = [
  { kind: 'start-session', label: messages.quickStartSession },
  { kind: 'brainstorm', label: messages.quickBrainstorm },
  { kind: 'dictate-notes', label: messages.quickDictateNotes },
  { kind: 'capture-work', label: messages.quickCaptureWork },
];

interface WorkspaceEmptyStateProps {
  onSelect: (kind: WorkspaceQuickStartKind) => void;
  busy?: boolean;
}

/**
 * Idle Voice canvas — Heidi greeting + four text chips above the composer.
 * Collapses once the clinician speaks or picks a chip (user transcript).
 */
export function WorkspaceEmptyState({ onSelect, busy }: WorkspaceEmptyStateProps) {
  const intl = useIntl();

  return (
    <div
      className="mx-auto flex size-full max-w-xl flex-col items-start justify-end gap-6 px-4 pb-2"
      data-testid="workspace-empty-state"
    >
      <div className="flex items-center gap-3">
        <HeidiLogoMark className="size-[17px]" />
        <TypographyH4 className="text-balance tracking-tight text-text-primary">
          {intl.formatMessage(messages.emptyTitle)}
        </TypographyH4>
      </div>

      <div className="grid w-full grid-cols-2 gap-4">
        {QUICK_STARTS.map(({ kind, label }) => (
          <button
            key={kind}
            type="button"
            onClick={() => onSelect(kind)}
            disabled={busy}
            className={cn(
              'flex h-[78px] w-full items-center justify-center rounded-3xl border border-border',
              'bg-surface p-4 shadow-sm',
              'text-text-secondary hover:bg-surface-hover disabled:opacity-50',
              focusRing
            )}
            data-testid={`workspace-quickstart-${kind}`}
          >
            <TypographyCaption className="text-center leading-none">
              {intl.formatMessage(label)}
            </TypographyCaption>
          </button>
        ))}
      </div>
    </div>
  );
}
